/** Touch swipe navigation — swipe left for next step, swipe right to go back. */
import { useEffect, useRef, useCallback } from 'react';
import { useSession } from '../SessionContext';

const MIN_SWIPE_DISTANCE = 50;

interface UseSwipeNavigationOptions {
  /** Called when swiping past an info step (sends server-side submit). */
  onSubmitInfo?: () => void;
  /** When true, forward swipe (left) is blocked. */
  blockNext?: boolean;
}

/**
 * Adds touch swipe navigation to the session player.
 * Swipe left → next step, swipe right → go back.
 * Only triggers on primarily horizontal swipes exceeding the minimum distance.
 */
export function useSwipeNavigation(options?: UseSwipeNavigationOptions): void {
  const { state, dispatch, canGoNext, canGoBack, currentStep } = useSession();
  const onSubmitInfo = options?.onSubmitInfo;
  const { isReviewing } = state;
  const blockNext = options?.blockNext ?? false;
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchStart.current) return;

      const dx = e.changedTouches[0].clientX - touchStart.current.x;
      const dy = e.changedTouches[0].clientY - touchStart.current.y;
      touchStart.current = null;

      // Must be primarily horizontal and exceed threshold
      if (Math.abs(dx) < MIN_SWIPE_DISTANCE || Math.abs(dy) > Math.abs(dx)) return;

      if (dx < 0 && canGoNext && !blockNext) {
        // Swipe left → next step
        if (currentStep?.stepKind === 'info' && onSubmitInfo && !isReviewing) {
          onSubmitInfo();
        } else {
          dispatch({ type: 'NEXT_STEP' });
        }
      } else if (dx > 0 && canGoBack) {
        // Swipe right → previous step
        dispatch({ type: 'GO_BACK' });
      }
    },
    [canGoNext, canGoBack, blockNext, dispatch, currentStep, onSubmitInfo, isReviewing]
  );

  useEffect(() => {
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchEnd]);
}
